/**
 * What GitLab says is in the way of a merge, in words a person can act on.
 *
 * GitLab names the blocker on a merge request in `detailed_merge_status`, and
 * BEST relays it untouched (`merge_status` in the payload). This file is the
 * ONLY place it is turned into a sentence, and it is deliberately a lookup and
 * not a rule: BEST encodes no review or merge policy, so nothing here decides
 * who may merge or what must be true first. It words what GitLab already
 * reported.
 *
 * The one judgement it does carry is `blocking`: whether merging can get past
 * the condition at all. A conflict or an unmet approval cannot be pressed
 * through, so the Merge button is disabled and says why. "Still checking" can
 * resolve in the next second, and a status BEST has never heard of is not
 * BEST's to veto, so neither disables anything - GitLab answers when the
 * button is pressed.
 *
 * GitLab adds values over time and BEST must not need a release to show a new
 * one, so an unlisted status is shown as itself in a neutral sentence rather
 * than hidden.
 */

export interface MergeBlocker {
  text: string
  /** `warning` for something in the way, `info` for "wait" and "not sure". */
  severity: 'warning' | 'info'
  /** True when merging cannot get past it; false when GitLab may still say yes. */
  blocking: boolean
}

const blocks = (text: string): MergeBlocker => ({ text, severity: 'warning', blocking: true })
const waits = (text: string): MergeBlocker => ({ text, severity: 'info', blocking: false })

const STILL_CHECKING = 'GitLab is still checking whether this can merge.'

/**
 * Each status GitLab documents, as a sentence. A function of the target so the
 * conflict message can name the branch the way it always has.
 */
const KNOWN: Record<string, (target: string) => MergeBlocker> = {
  conflict: (target) =>
    blocks(`GitLab reports conflicts with ${target}. They have to be resolved before this can merge.`),
  draft_status: () => blocks('This merge request is a draft. Mark it ready before it can merge.'),
  ci_still_running: () => blocks('The pipeline is still running. It has to finish before this can merge.'),
  ci_must_pass: () => blocks('The pipeline has to pass before this can merge.'),
  not_approved: () => blocks('It needs approval before it can merge.'),
  discussions_not_resolved: () => blocks('Every discussion has to be resolved before this can merge. They are listed under Discussion, below.'),
  need_rebase: () => blocks('The source branch has to be rebased before this can merge.'),
  requested_changes: () => blocks('A reviewer has requested changes.'),
  status_checks_must_pass: () => blocks('Every external status check has to pass before this can merge.'),
  security_policy_violations: () => blocks('A security policy has to be satisfied before this can merge.'),
  not_open: () => blocks('This merge request is not open.'),
  checking: () => waits(STILL_CHECKING),
  unchecked: () => waits(STILL_CHECKING),
  preparing: () => waits(STILL_CHECKING),
}

/**
 * The sentence for a merge status, or null when there is nothing to say.
 *
 * `mergeable` is nothing to say, and so is no status at all: an absent value
 * means GitLab did not report one, and BEST does not invent a blocker.
 */
export function mergeBlocker(
  status: string | null | undefined,
  target = 'the target branch',
): MergeBlocker | null {
  if (!status || status === 'mergeable') return null
  // hasOwn, not a bare lookup: the status is whatever GitLab sent, and a string
  // like 'constructor' would otherwise find something on Object's prototype.
  return Object.hasOwn(KNOWN, status)
    ? KNOWN[status](target)
    : waits(`GitLab reports: ${status}`)
}
